// src/context/ProfileContext.tsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useApi } from '../lib/useApi';
import type { UserProfile } from '../lib/useApi';
import { useUser } from '@clerk/clerk-react'; // CB:BEGIN check-signed-in

export const LEGAL_ACCEPTANCE_REQUIRED = 'LEGAL_ACCEPTANCE_REQUIRED';

type ProfileContextValue = {
  profile: UserProfile | null;
  homeownerId: string | null;
  loading: boolean;
  error: Error | null;
  requiresLegalAcceptance: boolean;
  refreshProfile: () => Promise<void>;
};

const ProfileContext = createContext<ProfileContextValue | undefined>(undefined);

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [requiresLegalAcceptance, setRequiresLegalAcceptance] = useState(false);
  const api = useApi();
  const { isSignedIn } = useUser(); // CB:BEGIN check-signed-in

  const load = useCallback(async () => {
    // CB:BEGIN skip-if-not-signed-in
    // Don't call API if user is not signed in - prevents 401 spam
    if (!isSignedIn) {
      setProfile(null);
      setLoading(false);
      setError(null);
      setRequiresLegalAcceptance(false);
      return;
    }
    // CB:END skip-if-not-signed-in
    
    setLoading(true);
    setError(null);
    setRequiresLegalAcceptance(false);
    try {
      const me = await api.getUserProfile();
      setProfile(me);
    } catch (err: any) {
      if (err?.message === "UNAUTHORIZED") {
        setProfile(null);
        setError(null);
        setRequiresLegalAcceptance(false);
      } else if (err?.message === LEGAL_ACCEPTANCE_REQUIRED) {
        setProfile(null);
        setError(null);
        setRequiresLegalAcceptance(true);
      } else {
        setError(new Error(err?.message || 'Failed to load profile'));
        setRequiresLegalAcceptance(false);
      }
    } finally {
      setLoading(false);
    }
  }, [api, isSignedIn]); // CB:BEGIN add-isSignedIn-dependency

  useEffect(() => {
    // CB:BEGIN skip-if-not-signed-in
    // Don't call API if user is not signed in - prevents 401 spam
    if (!isSignedIn) {
      setProfile(null);
      setLoading(false);
      setError(null);
      setRequiresLegalAcceptance(false);
      return;
    }
    // CB:END skip-if-not-signed-in
    
    let cancelled = false;
    
    async function loadProfile() {
      if (!isSignedIn) {
        setProfile(null);
        return;
      }
      
      try {
        const profile = await api.getUserProfile();
        if (!cancelled) setProfile(profile);
      } catch (err: any) {
        if (err?.message === "UNAUTHORIZED") {
          if (!cancelled) {
            setProfile(null);
            setError(null);
            setRequiresLegalAcceptance(false);
          }
          return;
        }
        if (err?.message === LEGAL_ACCEPTANCE_REQUIRED) {
          if (!cancelled) {
            setProfile(null);
            setError(null);
            setRequiresLegalAcceptance(true);
          }
          return;
        }
        // CB:BEGIN profile-context-api-unreachable
        const isNetworkError =
          err?.message === "API_UNREACHABLE" ||
          (err instanceof TypeError && (err?.message === 'Failed to fetch' || err?.message?.includes?.('fetch')));
        if (isNetworkError) {
          if (!cancelled) {
            setProfile(null);
            setError(new Error('API unavailable. Is the backend running?'));
            setRequiresLegalAcceptance(false);
          }
          return;
        }
        // CB:END profile-context-api-unreachable
        if (!cancelled) {
          console.error('[ProfileContext] Failed to load profile', err);
          setError(new Error(err?.message || 'Failed to load profile'));
          setRequiresLegalAcceptance(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    
    loadProfile();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]); // CB:BEGIN add-isSignedIn-dependency - Only run when sign-in status changes

  const value = useMemo<ProfileContextValue>(() => ({
    profile,
    homeownerId: profile?.id ?? null,
    loading,
    error,
    requiresLegalAcceptance,
    refreshProfile: load
  }), [profile, loading, error, requiresLegalAcceptance, load]);

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

export function useProfile() {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useProfile must be used within a ProfileProvider');
  return ctx;
}
